import { useEffect, useState } from "react";

/**
 * Reports whether `ref`'s element is currently in the viewport, updating in
 * BOTH directions as it enters and leaves. Unlike `useLoadOnVisible` (a
 * load-once trigger that disconnects after the first intersection), this keeps
 * observing for the element's whole lifetime.
 *
 * WEB-2593: used to pause off-screen looping videos so mobile GPUs don't run
 * out of concurrent decode streams.
 *
 * Options are destructured to primitives so an inline `{ rootMargin }` at the
 * call site doesn't recreate the observer on every render.
 */
const useIsVisible = (ref, { root = null, rootMargin = "0px", threshold = 0 } = {}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { root, rootMargin, threshold }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [ref, root, rootMargin, threshold]);

  return isVisible;
};

export default useIsVisible;
